/* import __COLOCATED_TEMPLATE__ from './eisa-scoring.hbs'; */
import Component from '@glimmer/component';

const pointsEISA = [50, 47, 44, 41, 39, 37, 35, 33, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];

export default class EisaScoringComponent extends Component
{
	// All EISA teams to include in filtered results
	get teams()
	{
		return this.args.config?.teams || [];
	}

	// Teams that actually score points (subset of teams)
	get scoringTeams()
	{
		return this.args.config?.scoringTeams || [];
	}

	get nordicRaces()
	{
		return this.args.config?.nordic?.races || [];
	}

	scoreRace(raceConfig)
	{
		const allResults = this.args.results?.[raceConfig.resultsFile] || [];
		// Filter by class group (use loose equality for string/number compatibility)
		const classResults = allResults.filter(r => r.ClassGroupId == raceConfig.classGroupId);

		// Filter to all EISA teams with OK status, sorted by original place
		const eisaResults = classResults.filter(r =>
			this.teams.includes(r.club) && r.status == 'OK'
		).sort((a, b) => a.place - b.place);

		// Assign EISA place
		eisaResults.forEach((r, i) => r.eisaPlace = i + 1);

		// Handle ties - group by ORIGINAL place, average points based on EISA positions
		// Only scoring teams get points
		let i = 0;
		while (i < eisaResults.length)
		{
			let j = i;
			// Find all EISA athletes with the same ORIGINAL place (they were tied)
			while (j < eisaResults.length && eisaResults[j].place === eisaResults[i].place)
			{
				j++;
			}
			// i to j-1 are tied (same original place)
			const tiedCount = j - i;
			// Sum up the points for the EISA positions these tied athletes occupy
			let totalPoints = 0;
			for (let k = i; k < j; k++)
			{
				totalPoints += pointsEISA[k] || 0; // k is the 0-indexed EISA position
			}
			const avgPoints = totalPoints / tiedCount;
			for (let k = i; k < j; k++)
			{
				// Only assign points to scoring teams
				if (this.scoringTeams.includes(eisaResults[k].club))
				{
					eisaResults[k].eisaPoints = avgPoints;
				}
				else
				{
					eisaResults[k].eisaPoints = 0;
				}
			}
			i = j;
		}

		// Group by team, limit 3 scorers per team (only for scoring teams)
		const teamScorers = {};
		for (const r of eisaResults)
		{
			if (!this.scoringTeams.includes(r.club)) continue;
			if (!teamScorers[r.club]) teamScorers[r.club] = [];
			if (teamScorers[r.club].length < 3)
			{
				teamScorers[r.club].push(r);
			}
		}

		// Calculate team totals
		const teamScores = {};
		for (const [team, scorers] of Object.entries(teamScorers))
		{
			teamScores[team] = scorers.reduce((sum, r) => sum + r.eisaPoints, 0);
		}

		return { scorers: eisaResults, teamScorers, teamScores };
	}

	get nordicScores()
	{
		return this.nordicRaces.map((race, index) => ({
			...race,
			raceIndex: index,
			...this.scoreRace(race)
		}));
	}

	get teamTotals()
	{
		const totals = {};

		// Initialize all scoring teams
		for (const team of this.scoringTeams)
		{
			totals[team] = {
				name: team,
				nordicDay1Men: 0,
				nordicDay1Women: 0,
				nordicDay2Men: 0,
				nordicDay2Women: 0,
				nordicTotal: 0,
				alpineGSMen: 0,
				alpineGSWomen: 0,
				alpineSLMen: 0,
				alpineSLWomen: 0,
				alpineTotal: 0,
				carnivalTotal: 0
			};
		}

		// Accumulate scores from each race
		for (const race of this.nordicScores)
		{
			const isMen = race.gender === 'M';
			const isDay1 = race.day === 1;
			for (const [team, score] of Object.entries(race.teamScores || {}))
			{
				if (totals[team])
				{
					if (isMen && isDay1)
					{
						totals[team].nordicDay1Men += score;
					}
					else if (!isMen && isDay1)
					{
						totals[team].nordicDay1Women += score;
					}
					else if (isMen && !isDay1)
					{
						totals[team].nordicDay2Men += score;
					}
					else
					{
						totals[team].nordicDay2Women += score;
					}
				}
			}
		}

		// Calculate totals
		for (const team of Object.values(totals))
		{
			team.nordicTotal = team.nordicDay1Men + team.nordicDay1Women + team.nordicDay2Men + team.nordicDay2Women;
			team.alpineTotal = team.alpineGSMen + team.alpineGSWomen + team.alpineSLMen + team.alpineSLWomen;
			team.carnivalTotal = team.nordicTotal + team.alpineTotal;
		}

		// Convert to sorted array (by carnival total descending)
		return Object.values(totals)
			.filter(t => t.carnivalTotal > 0)
			.sort((a, b) => b.carnivalTotal - a.carnivalTotal);
	}

	// Helper getters for displaying races by gender
	get nordicMenRaces()
	{
		return this.nordicScores.filter(r => r.gender === 'M');
	}

	get nordicWomenRaces()
	{
		return this.nordicScores.filter(r => r.gender === 'F');
	}
}
